import React, { useState } from 'react';
import { FaRobot, FaPaperPlane, FaSync, FaMagic } from 'react-icons/fa';
import '../../styles/Files.css';

const AIOrganizer = ({ onApplySuggestions }) => {
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: "Hello! I can help you organize your 3D model files. You can ask me to create folders or move files based on different criteria like file type, themes, or projects."
    }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleInputChange = (e) => {
    setChatInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if (!chatInput.trim()) return;

    // Add user message to chat
    const userMessage = {
      sender: 'user',
      text: chatInput
    };
    
    setMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsProcessing(true);

    // In a real implementation, this would send the message to an AI service
    // For now, we'll simulate a response with dummy data
    setTimeout(() => {
      let aiResponse;
      
      if (chatInput.toLowerCase().includes('create folder') || 
          chatInput.toLowerCase().includes('make folder')) {
        aiResponse = {
          sender: 'ai',
          text: "I've created the folder for you. You can now drag and drop files into it.",
          suggestion: {
            type: 'create_folder',
            folderName: chatInput.includes('character') ? 'Characters' : 'Project Files',
            parentFolder: 'root'
          }
        };
      } else if (chatInput.toLowerCase().includes('organize') || 
                 chatInput.toLowerCase().includes('sort') || 
                 chatInput.toLowerCase().includes('categorize')) {
        aiResponse = {
          sender: 'ai',
          text: "I've analyzed your files and suggested a new organization. Would you like me to apply this structure?",
          suggestion: {
            type: 'reorganize',
            structure: [
              { name: 'Characters', files: ['character1.stl', 'hero.stl'] },
              { name: 'Terrain', files: ['mountain.stl', 'castle.stl'] },
              { name: 'Props', files: ['sword.stl', 'shield.stl'] }
            ]
          }
        };
      } else if (chatInput.toLowerCase().includes('move') || 
                 chatInput.toLowerCase().includes('put')) {
        aiResponse = {
          sender: 'ai',
          text: "I've moved the file(s) as requested.",
          suggestion: {
            type: 'move_files',
            files: ['file1.stl'],
            targetFolder: 'Characters'
          }
        };
      } else {
        aiResponse = {
          sender: 'ai',
          text: "I can help you organize your files. Try asking me to:\n" +
                "- Create a new folder for a specific type of models\n" +
                "- Move files into specific folders\n" +
                "- Organize all your files by category\n" +
                "- Sort files by size or date"
        };
      }
      
      setMessages(prev => [...prev, aiResponse]);
      setIsProcessing(false);
    }, 1500);
  };

  const applySuggestion = (suggestion) => {
    // Call the provided callback if available
    if (onApplySuggestions) {
      onApplySuggestions(suggestion);
    }
    
    setMessages(prev => [
      ...prev, 
      {
        sender: 'ai',
        text: "Changes applied successfully!"
      }
    ]);
  };

  const requestAutoOrganize = () => {
    setIsProcessing(true);
    
    // In a real implementation, this would call the backend AI service
    // For now, we'll simulate a response with dummy data
    setTimeout(() => {
      const aiResponse = {
        sender: 'ai',
        text: "I've analyzed your files and created an organization structure based on their content and purpose. Would you like me to apply this structure?",
        suggestion: {
          type: 'reorganize',
          structure: [
            { name: 'Characters', files: ['character1.stl', 'hero.stl'] },
            { name: 'Terrain', files: ['mountain.stl', 'castle.stl'] },
            { name: 'Props', files: ['sword.stl', 'shield.stl'] },
            { name: 'Sci-Fi', files: ['spaceship.stl', 'robot.stl'] },
            { name: 'Fantasy', files: ['dragon.stl', 'wizard.stl'] }
          ]
        }
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsProcessing(false);
    }, 2000);
  };

  return (
    <div className="ai-organizer">
      <div className="auto-organize-header">
        <button 
          className="auto-organize-btn" 
          onClick={requestAutoOrganize}
          disabled={isProcessing}
        >
          <FaMagic /> Auto-Organize All Files
        </button>
        <p className="auto-organize-info">
          Let AI analyze and organize your files into a logical folder structure
        </p>
      </div>
      
      <div className="chat-messages">
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`message ${message.sender === 'ai' ? 'ai-message' : 'user-message'}`}
          >
            {message.sender === 'ai' && (
              <div className="ai-icon">
                <FaRobot />
              </div>
            )}
            
            <div className="message-content">
              <p>{message.text}</p>
              
              {message.suggestion && (
                <div className="suggestion">
                  <button 
                    className="apply-suggestion-btn"
                    onClick={() => applySuggestion(message.suggestion)}
                  >
                    Apply Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isProcessing && (
          <div className="message ai-message">
            <div className="ai-icon">
              <FaRobot />
            </div>
            <div className="message-content thinking">
              <FaSync className="fa-spin" />
              <span>Thinking...</span>
            </div>
          </div>
        )}
      </div>
      
      <div className="chat-input">
        <input
          type="text"
          placeholder="Ask the AI to help organize your files..."
          value={chatInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isProcessing}
        />
        <button 
          className="send-button" 
          onClick={sendMessage}
          disabled={isProcessing || !chatInput.trim()}
        >
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

export default AIOrganizer;